import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { usePostMeterReadingStatus, useUpdateMeterReadingStatus, useMeterReadingStatusList, useReadingStatus } from "@features/mx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useToast } from "@shared/hooks/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface AddConditionStatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editData?: any;
}

export const AddConditionStatusModal: React.FC<AddConditionStatusModalProps> = ({
  open,
  onOpenChange,
  editData,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const { data: statusListResponse } = useReadingStatus();

  const systemStatusOptions = React.useMemo(() => {
    const list = statusListResponse?.result || [];
    return list.map((item: any) => ({
      label: item.value,
      value: String(item.key),
    }));
  }, [statusListResponse]);

  const { mutate: createStatus } = usePostMeterReadingStatus();
  const { mutate: updateStatus } = useUpdateMeterReadingStatus();

  const formFields: FormField[] = [
    {
      name: "statusName",
      label: "Status Name",
      type: "text",
      required: true,
      placeholder: "e.g. Good, Damaged, Needs Replacement",
    },
    {
      name: "systemStatus",
      label: "System Status",
      type: "select",
      required: true,
      placeholder: "Select system status",
      options: systemStatusOptions,
      className: "mt-4",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  useEffect(() => {
    if (open) {
      if (editData) {
        form.reset({
          statusName: editData.name,
          systemStatus: editData.codeId,
        });
      } else {
        form.reset({
          statusName: "",
          systemStatus: "",
        });
      }
    }
  }, [open, editData]);

  const handleSubmit = (data: any) => {
    const payload = {
      name: data.statusName,
      code_id: data.systemStatus,
      remote_utility_id: getRemoteUtilityId(),
    };

    if (editData) {
      updateStatus(
        {
          id: Number(editData.id),
          payload,
        },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: "Condition status updated successfully.",
            });
            queryClient.invalidateQueries({
              queryKey: ["mx", "settings", "meterReadingStatusList"],
            });
            onOpenChange(false);
          },
        }
      );
    } else {
      createStatus(payload as any, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Condition status added successfully.",
          });
          queryClient.invalidateQueries({
            queryKey: ["mx", "settings", "meterReadingStatusList"],
          });
          onOpenChange(false);
        },
      });
    }
  };

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 mt-6">
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
        Cancel
      </Button>
      <Button type="submit" disabled={!form.formState.isValid}>
        {editData ? "Save Changes" : "Add Status"}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {editData ? "Edit Condition Status" : "Add Condition Status"}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            config={{
              showSubmitButton: false,
              classes: FIELD_CLASSES,
            }}
            renderCustomFooter={renderCustomFooter}
            gridColumns={1}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddConditionStatusModal;
